/**
 * @fileoverview PortPuffin Terms of Service registry data.
 *
 * Port-web has no existing duplicate source. Content is authored
 * fresh in the cross-tenant ecosystem voice modeled on sibling apps
 * (planner-web's structure, adapted to PortPuffin's voice + AI
 * receptionist domain).
 *
 * Includes port-only sections covering call recording consent (#4)
 * and AI-generated voice content (#7) reflecting the regulated nature
 * of voice processing + voice cloning.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'port',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PortPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-05-04',
    contactEmail: 'legal@portpuffin.com',
    canonicalUrl: 'https://portpuffin.com/terms',
    ogImage: 'https://portpuffin.com/og-image.jpg',
    description:
      'PortPuffin terms of service. Review the terms governing use of our business voice and AI receptionist platform.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName}, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our services. These terms apply to all users, including businesses and individuals who access {productName} for business voice, IVR, AI receptionist, or call recording services.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides business voice and AI receptionist services for shipping, mailbox, print, and retail centers. Our platform enables phone-number provisioning, IVR authoring, business-hours routing, call queueing, voicemail capture and transcription, ASR streaming, AI receptionist orchestration, voice cloning, call recording with state-aware consent, and emergency keyword detection.',
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized access.',
        },
      ],
    },
    {
      id: 'recording-consent-and-state-law-compliance',
      title: '4. Recording Consent and State Law Compliance',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} captures call recordings only when you explicitly enable recording for a given phone number. When enabled, our platform automatically prepends a state-aware consent announcement before audio capture begins, satisfying applicable two-party-consent state law (14 states + DC) and one-party-consent state law (the remaining 37 states). You are responsible for maintaining accurate location metadata for each phone number to ensure the correct consent announcement applies.',
        },
        {
          type: 'paragraph',
          text: 'You may not disable the consent announcement in two-party-consent jurisdictions. Doing so violates these Terms and applicable wiretap law.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '5. Subscription and Billing',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is offered on a subscription basis with usage-based components for phone numbers, call minutes, transcription minutes, and AI usage. Pricing is documented at portpuffin.com/pricing and may be updated with 30 days notice. You may cancel your subscription at any time. No refunds are provided for partial billing periods.',
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '6. Acceptable Use',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You agree to use {productName} only for lawful purposes and in accordance with these terms. You may not use our services to make unsolicited or unlawful calls, defraud callers, evade do-not-call regulations, impersonate another party using voice cloning, or engage in any activity that violates applicable telephone, wiretap, TCPA, or DNC laws.',
        },
      ],
    },
    {
      // The id is an ANCHOR (linked from outside), not copy — it keeps its slug (TERM-002).
      id: 'tenant-tcpa-and-dnc-obligations',
      title: '7. Your TCPA, DNC, and Consent Obligations',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'You acknowledge that the Telephone Consumer Protection Act (TCPA), the FCC Do-Not-Call (DNC) rules, and analogous state-level rules apply to outbound voice + SMS contact you initiate using {productName}. We provide the compliance gate (federated DNC list, per-channel consent records, six-step block-decision audit trail, and subpoena-ready evidence export); you remain the party responsible for the dispatch decision and for the legal posture of the contact.',
        },
        {
          type: 'paragraph',
          text: 'You may disable the platform-level DNC enforcement flag (`port.compliance.dnc_enforcement_enabled`) at your election. Doing so transfers all TCPA / DNC liability for the resulting outbound traffic to you. We will not indemnify you for any TCPA, DNC, or analogous claim arising from contact dispatched after you disable the enforcement flag.',
        },
        {
          type: 'paragraph',
          text: 'When you use the platform consent-capture form builder (in-store kiosk, web widget, SMS-reply, verbal-recorded, paper-form, or widget-embed source), the disclosure language presented at capture time is your customer-facing contractual record. You may not delete or alter the disclosed language post-grant; the platform stores both the disclosed text and a cryptographic snapshot of the consent form source.',
        },
        {
          type: 'paragraph',
          text: 'You are responsible for verifying that each trigger-type you configure for outbound contact maps to a real Established Business Relationship (EBR) under the FCC EBR framework. The platform does not classify EBRs on your behalf; the EBR mapping table in our Voice Compliance Guide is a reference, not a determination.',
        },
        {
          type: 'paragraph',
          text: 'On lawful subpoena or TCPA-plaintiff discovery, we will fulfill the evidence-export request within 60 seconds of receipt for any single company + channel + recipient query. Records of every block decision logged by the six-step gate, every consent record, and every per-channel disclosure are available via this endpoint.',
        },
        {
          type: 'paragraph',
          text: 'Cross-app sibling ParcelPuffin Colony services (PrepPuffin, PalmPuffin, PlannerPuffin, PayDayPuffin) may query the platform`s internal compliance-check API to gate their own dispatches. That query does not transfer your TCPA liability for the underlying contact decision — you remain the dispatching party.',
        },
      ],
    },
    {
      id: 'voice-cloning-and-ai-content',
      title: '8. Voice Cloning and AI-Generated Content',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'AI-generated voice content (TTS playback, AI receptionist responses, cloned voices) is provided as a draft for human review. {productName} does not guarantee the accuracy, completeness, or suitability of AI-generated content for any specific regulatory or compliance purpose.',
        },
        {
          type: 'paragraph',
          text: 'Voice cloning requires explicit attestation that you hold rights to the cloned voice. Cloned voices are watermarked using industry-standard PerTH watermarking and are subject to DMCA hard-delete on rights challenge. You may not clone the voice of any person without their explicit, documented consent.',
        },
      ],
    },
    {
      id: 'data-and-privacy',
      title: '9. Data and Privacy',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'Your use of {productName} is also governed by our Privacy Policy, which describes how we collect, use, and protect your data. By using our services, you consent to the data practices described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '10. Limitation of Liability',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides voice routing and AI receptionist tools but does not guarantee specific call-handling outcomes, AI accuracy, or zero-downtime availability. Carrier decisions on call routing and number portability are made by the carriers themselves. Our liability is limited to the amount you paid for the service in the preceding 12 months.',
        },
      ],
    },
    {
      id: 'termination',
      title: '11. Termination',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may suspend or terminate your access to {productName} if you violate these terms. You may cancel your account at any time. Upon termination, your right to use the service ceases immediately, though we will retain call recordings and audit logs as described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '12. Changes to Terms',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these terms at any time. We will provide notice of material changes via email or through our platform. Continued use of {productName} after changes take effect constitutes acceptance of the revised terms.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '13. Contact Us',
      order: 13,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
